// @flow

// Libs
import React, { Component } from 'react'
import _indexOf from 'lodash/indexOf'
import _isEqual from 'lodash/isEqual'
import _slice from 'lodash/slice'
import _omitBy from 'lodash/omitBy'
import _omit from 'lodash/omit'

// Styles
import { textInputStyle, placeholderStyle, inputWrapperStyle, inputStyle, focusBorder } from './../styles'
import { lightGrey } from 'hfty-styles/src/colors'

// Utilities
import nAf from 'fc-utils/nAf'

// Types
type Props = {
  icon: ?React.Element<any>,
  label: string,
  placeholder: string,
  hidePlaceholder: boolean,
  hideLabel: boolean,
  hidden: boolean,
  required: boolean,
  name: string,
  type: string,
  focusStyle: Object,
  onChange: Function,
  onBlur: Function,
  onFocus: Function,
  isValid: boolean,
  style: Object,
  value: ?string,
}

export default class TextInput extends Component {
  props: Props
  onChange: Function
  onFocus: Function
  onBlur: Function
  setRef: Function
  setFocus: Function

  constructor (props: Props) {
    super(props)

    this.state = {
      focused: false,
      touched: false,
    }

    this.onChange = this.onChange.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.setRef = this.setRef.bind(this)
    this.setFocus = this.setFocus.bind(this)
  }

  setRef<I> (_input: I): * {
    if (_input) {
      this._input = _input
    }
  }

  setFocus () {
    setTimeout(() => {
      const { focused } = this.state

      if (!focused && this._input) {
        this._input.focus()
      }
    }, 150)
  }

  onChange<Event> (e: Event): * {
    this.setState({
      touched: true,
    })
    this.props.onChange(e)
  }

  onFocus<Event> (e: Event): * {
    if (this._input) {
      this._input.select()
    }

    this.setState({
      focused: true,
    })
    this.props.onFocus(e)
  }

  onBlur<Event> (e: Event): * {
    this.setState({
      focused: false,
      touched: true,
    })

    this.props.onBlur(e)
  }

  eventProps (): * {
    const defaultEventProps = ['onChange', 'onFocus', 'onBlur']

    const omit = (prop: *, name: string): * => {
      const isDefault = _indexOf(defaultEventProps, name) >= 0
      const on = !isDefault && _isEqual(['o', 'n'], _slice(name, 0, 2))
      const isFn = on && typeof prop === 'function'

      return !isFn
    }

    return _omitBy(this.props, omit)
  }

  renderIcon (): ?React.Element<any> {
    const { icon } = this.props

    if (icon) {
      return React.cloneElement(icon, {
        color: lightGrey(0.6),
      })
    }

    return null
  }

  renderPlaceholder ({focused, touched, inline, valid}: Object): ?React.Element<any> {
    const { label, placeholder, required, icon, hidePlaceholder } = this.props

    if (hidePlaceholder) {
      return null
    }

    return (
      <div
        onClick={this.setFocus}
        style={placeholderStyle({
          focused,
          touched,
          inline,
          valid,
          left: icon ? 30 : 0,
        })}
      >
        {placeholder || <span>{label} {required ? '*' : ''}</span>}
      </div>
    )
  }

  getValue (): ?string {
    const { value } = this.props

    if (!value) {
      return ''
    }

    return value
  }

  isValid (value: string = this.getValue()): boolean {
    const { isValid } = this.props

    if (typeof isValid === 'function') {
      return isValid(value)
    }

    return isValid
  }

  render (): React.Element<any> {
    if (this.props.hidden) {
      return null
    }

    const { name, label, required, style, type, icon, focusStyle, hideLabel } = this.props
    const { focused, touched } = this.state
    const value = this.getValue()
    const valid = (!required || (required && value)) && this.isValid(value)
    const inline = !value

    return (
      <div
        data-test='TextInput'
        onClick={this.setFocus}
      >
        <div style={textInputStyle({focused})}>
          {!hideLabel && this.renderPlaceholder({focused, touched, inline, valid})}
          <div style={inputWrapperStyle()}>
            {this.renderIcon()}
            <input
              ref={this.setRef}
              name={name}
              required={required}
              style={{...inputStyle({left: icon ? 5 : 0}), ...(_omit(style, 'backgroundColor'))}}
              type={type}
              value={this.getValue()}
              onChange={this.onChange}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              placeholder={label}
              {...this.eventProps()}
            />
          </div>
          <div style={{...focusBorder({touched, focused, valid}), ...focusStyle}} />
        </div>
      </div>
    )
  }
}

TextInput.defaultProps = {
  name: '',
  value: '',
  onChange: nAf,
  onFocus: nAf,
  onBlur: nAf,
  isValid: true,
  required: false,
  disabled: false,
  style: {},
  type: 'text',
  placeholder: false,
  icon: null,
  focusStyle: {},
}
